import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  styled,
  alpha,
  useTheme,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import NotificationSnackbar from "./NotificationBar"; // Assuming this is in the same folder

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "650px",
  overflowX: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",
  "&::-webkit-scrollbar": { height: "8px", width: "8px" },
  "&::-webkit-scrollbar-track": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.25),
    borderRadius: "4px",
    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.4) },
  },
}));

function ViewPayrollApprovers({ entitySectorId, onBack }) {
  const theme = useTheme();
  const PRIMARY_COLOR = "#0a2e52";
  const SECONDARY_COLOR = "#1565C0";
  const HEADER_BG_COLOR = "#f4f7fa";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rawRoles, setRawRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // State for the Delete Dialog
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Snackbar State
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleSnackbarClose = () => setSnackbarOpen(false);

  // Wrapped in useCallback so we can call it again after deletion to refresh data
  const fetchApprovers = useCallback(async () => {
    if (!entitySectorId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        `/api/support/payroll-approver-by-entity-sector?entitySectorId=${entitySectorId}`,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch payroll approvers");
      }
      const data = await response.json();
      setRawRoles(data[0]?.roles || []);
    } catch (err) {
      console.error("Error fetching approvers:", err);
      setError("Could not load payroll approvers. Please try again later.");
      setRawRoles([]);
    } finally {
      setLoading(false);
    }
  }, [entitySectorId]);

  useEffect(() => {
    fetchApprovers();
  }, [fetchApprovers]);

  const approversData = useMemo(() => {
    if (!rawRoles || rawRoles.length === 0) return [];
    const roleOrder = { HR: 1, DAF: 2, CBM: 3 };
    const sortedRoles = [...rawRoles].sort((a, b) => {
      const orderA = roleOrder[a.role?.toUpperCase()] || 99;
      const orderB = roleOrder[b.role?.toUpperCase()] || 99;
      return orderA - orderB;
    });
    const term = searchTerm.trim().toLowerCase();
    let filteredRoles = sortedRoles;
    if (term) {
      filteredRoles = sortedRoles
        .map((roleObj) => {
          const filteredUsers = (roleObj.users || [])
            .map((user) => {
              const userMatches = user.name?.toLowerCase().includes(term);
              const filteredCCs = (user.costCenters || []).filter(
                (cc) =>
                  cc.costcenter?.toLowerCase().includes(term) ||
                  cc.costcenterId?.toLowerCase().includes(term),
              );
              if (userMatches) {
                return user;
              } else if (filteredCCs.length > 0) {
                return { ...user, costCenters: filteredCCs };
              }
              return null;
            })
            .filter(Boolean);
          return { ...roleObj, users: filteredUsers };
        })
        .filter((roleObj) => roleObj.users.length > 0);
    }
    const flattened = [];
    filteredRoles.forEach((roleObj) => {
      const users = roleObj.users || [];
      let roleSpan = 0;
      users.forEach((user) => {
        roleSpan += Math.max(user.costCenters?.length || 0, 1);
      });
      roleSpan = Math.max(roleSpan, 1);
      if (users.length === 0) {
        flattened.push({
          isRoleFirst: true,
          isRoleLast: true,
          roleSpan,
          role: roleObj.role,
          isUserFirst: true,
          userSpan: 1,
          user: null,
          cc: null,
        });
        return;
      }
      users.forEach((user, userIdx) => {
        const costCenters = user.costCenters || [];
        const userSpan = Math.max(costCenters.length, 1);
        const isLastUser = userIdx === users.length - 1;
        if (costCenters.length === 0) {
          flattened.push({
            isRoleFirst: userIdx === 0,
            isRoleLast: isLastUser,
            roleSpan,
            role: roleObj.role,
            isUserFirst: true,
            userSpan,
            user,
            cc: null,
          });
          return;
        }
        costCenters.forEach((cc, ccIdx) => {
          const isLastCC = ccIdx === costCenters.length - 1;
          flattened.push({
            isRoleFirst: userIdx === 0 && ccIdx === 0,
            isRoleLast: isLastUser && isLastCC,
            roleSpan,
            role: roleObj.role,
            isUserFirst: ccIdx === 0,
            userSpan,
            user,
            cc,
          });
        });
      });
    });
    return flattened;
  }, [rawRoles, searchTerm]);

  const handleClearSearch = () => setSearchTerm("");

  // --- Deletion Handlers ---
  const handleOpenDelete = (row) => {
    setDeleteTarget(row);
  };

  const handleCloseDelete = () => {
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async (deleteType) => {
    if (!deleteTarget) return;

    const actionKey =
      deleteType === "COST_CENTER"
        ? "Remove_Only_CostCenter"
        : "Remove_Entire_Sector";

    const payload = {
      entitySectorId: entitySectorId,
      costcenterId: deleteTarget.cc.costcenterId,
      userId: deleteTarget.user.userId,
      Action: actionKey,
    };

    try {
      setIsDeleting(true);
      const response = await fetch("/api/support/remove-role", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        setSnackbarMessage(result.message || "Removed successfully.");
        setSnackbarSeverity("success");
        // Refresh data
        await fetchApprovers();
      } else {
        setSnackbarMessage(result.message || "Failed to remove assignment.");
        setSnackbarSeverity("error");
      }
    } catch (err) {
      console.error("Delete request error:", err);
      setSnackbarMessage("An error occurred while deleting.");
      setSnackbarSeverity("error");
    } finally {
      setIsDeleting(false);
      setSnackbarOpen(true);
      setDeleteTarget(null);
    }
  };

  const getApprovalChipProps = (level) => {
    switch (level?.toUpperCase()) {
      case "APPROVED":
        return {
          bg: alpha(theme.palette.success.main, 0.15),
          color: theme.palette.success.dark,
        };
      case "VERIFIED":
        return {
          bg: alpha(theme.palette.info.main, 0.15),
          color: theme.palette.info.dark,
        };
      case "GENERATED":
        return {
          bg: alpha(theme.palette.warning.main, 0.15),
          color: theme.palette.warning.dark,
        };
      default:
        return {
          bg: alpha(theme.palette.grey[500], 0.15),
          color: theme.palette.grey[800],
        };
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        p: { xs: 2, sm: 3 },
        mb: 3,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        minHeight: "75vh",
      }}
    >
      {/* HEADER SECTION */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        {onBack && (
          <IconButton
            onClick={onBack}
            sx={{
              color: PRIMARY_COLOR,
              bgcolor: alpha(PRIMARY_COLOR, 0.05),
              "&:hover": { bgcolor: alpha(PRIMARY_COLOR, 0.1) },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: PRIMARY_COLOR }}
          >
            Sector Payroll Approvers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Viewing approval workflow roles and assigned personnel
          </Typography>
        </Box>
      </Box>

      {/* TOP CONTROL BAR: SEARCH & ADD BUTTON */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 4,
          borderRadius: 2,
          border: `1px solid ${alpha(PRIMARY_COLOR, 0.1)}`,
          bgcolor: alpha(PRIMARY_COLOR, 0.015),
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <TextField
          placeholder="Search Name, Cost Center or ID..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {searchTerm && (
                  <IconButton
                    aria-label="clear"
                    size="small"
                    onClick={handleClearSearch}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, maxWidth: 450, bgcolor: "white", borderRadius: 1 }}
        />

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{
            backgroundColor: SECONDARY_COLOR,
            "&:hover": { backgroundColor: PRIMARY_COLOR },
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            py: 1,
            borderRadius: "8px",
          }}
        >
          Assign New User
        </Button>
      </Paper>

      {/* CONTENT AREA */}
      <Box sx={{ width: "100%" }}>
        {/* LOADING STATE */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: PRIMARY_COLOR }} />
          </Box>
        )}

        {/* ERROR STATE */}
        {error && !loading && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography
              color="error"
              sx={{ fontSize: "0.95rem", fontWeight: 500 }}
            >
              {error}
            </Typography>
          </Box>
        )}

        {/* EMPTY STATE */}
        {!loading && !error && approversData.length === 0 && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">
              No matching payroll approvers found.
            </Typography>
          </Box>
        )}

        {/* DATA TABLE */}
        {!loading && !error && approversData.length > 0 && (
          <ResponsiveTableContainer>
            <Table stickyHeader size="medium">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      backgroundColor: HEADER_BG_COLOR,
                      width: "10%",
                    }}
                  >
                    Role
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      backgroundColor: HEADER_BG_COLOR,
                      width: "25%",
                    }}
                  >
                    User Details
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      backgroundColor: HEADER_BG_COLOR,
                      width: "25%",
                    }}
                  >
                    Cost Center
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      backgroundColor: HEADER_BG_COLOR,
                      width: "15%",
                    }}
                  >
                    Approval Level
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      backgroundColor: HEADER_BG_COLOR,
                      width: "15%",
                    }}
                  >
                    Assigned By
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 700,
                      backgroundColor: HEADER_BG_COLOR,
                      width: "10%",
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {approversData.map((row, index) => {
                  const chipStyle = row.cc
                    ? getApprovalChipProps(row.cc.approvalLevel)
                    : null;
                  const rowBorderStyle = row.isRoleLast
                    ? `3px solid ${alpha(SECONDARY_COLOR, 0.4)}`
                    : `1px solid ${theme.palette.divider}`;

                  return (
                    <TableRow key={index} hover>
                      {/* ROLE COLUMN */}
                      {row.isRoleFirst && (
                        <TableCell
                          rowSpan={row.roleSpan}
                          sx={{
                            fontWeight: 800,
                            color: PRIMARY_COLOR,
                            verticalAlign: "top",
                            backgroundColor: alpha(PRIMARY_COLOR, 0.02),
                            borderBottom: rowBorderStyle,
                          }}
                        >
                          {row.role}
                        </TableCell>
                      )}

                      {/* USER COLUMN */}
                      {row.isUserFirst && (
                        <TableCell
                          rowSpan={row.userSpan}
                          sx={{
                            verticalAlign: "top",
                            borderBottom: rowBorderStyle,
                          }}
                        >
                          {row.user ? (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  color: PRIMARY_COLOR,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <PersonIcon
                                  sx={{ fontSize: 18, color: SECONDARY_COLOR }}
                                />
                                {row.user.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  ml: 0.5,
                                }}
                              >
                                <AccountBoxIcon sx={{ fontSize: 16 }} />
                                ID: {row.user.userId}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No Users Assigned
                            </Typography>
                          )}
                        </TableCell>
                      )}

                      {/* COST CENTER COLUMN */}
                      <TableCell
                        sx={{ fontWeight: 500, borderBottom: rowBorderStyle }}
                      >
                        {row.cc ? (
                          <Box>
                            <Typography variant="body2">
                              {row.cc.costcenter}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ID: {row.cc.costcenterId}
                            </Typography>
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      {/* APPROVAL LEVEL COLUMN */}
                      <TableCell sx={{ borderBottom: rowBorderStyle }}>
                        {row.cc ? (
                          <Chip
                            label={row.cc.approvalLevel}
                            size="small"
                            sx={{
                              backgroundColor: chipStyle.bg,
                              color: chipStyle.color,
                              fontWeight: 700,
                              borderRadius: "6px",
                              fontSize: "0.75rem",
                            }}
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      {/* ASSIGNED/CREATED BY COLUMN */}
                      <TableCell sx={{ borderBottom: rowBorderStyle }}>
                        {row.cc ? (
                          <Typography variant="body2" color="text.secondary">
                            {row.cc.createdBy}
                          </Typography>
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      {/* ACTION COLUMN (Delete Icon) */}
                      <TableCell
                        align="center"
                        sx={{ borderBottom: rowBorderStyle }}
                      >
                        {row.cc && (
                          <IconButton
                            color="error"
                            onClick={() => handleOpenDelete(row)}
                            size="small"
                            sx={{
                              backgroundColor: alpha(
                                theme.palette.error.main,
                                0.05,
                              ),
                              "&:hover": {
                                backgroundColor: alpha(
                                  theme.palette.error.main,
                                  0.15,
                                ),
                              },
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ResponsiveTableContainer>
        )}
      </Box>

      {/* --- DELETE CONFIRMATION DIALOG --- */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={handleCloseDelete}
        sx={{
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            overflow: "visible",
            border: `1px solid ${alpha(theme.palette.primary.light, 0.5)}`,
            minWidth: "400px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: PRIMARY_COLOR,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Delete Assignment
          <IconButton
            onClick={handleCloseDelete}
            size="small"
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.primary" }}>
            You are about to remove an assignment for{" "}
            <strong>{deleteTarget?.user?.name}</strong> (Role:{" "}
            {deleteTarget?.role}).
          </DialogContentText>
          <DialogContentText
            sx={{ mt: 2, color: "text.secondary", fontSize: "0.9rem" }}
          >
            How would you like to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: "column",
            gap: 1.5,
            p: 2,
            alignItems: "stretch",
          }}
        >
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleConfirmDelete("COST_CENTER")}
            disabled={isDeleting}
            sx={{ fontWeight: 600, textTransform: "none", py: 1 }}
          >
            Remove only on payroll approver
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleConfirmDelete("SECTOR")}
            disabled={isDeleting}
            sx={{ fontWeight: 600, textTransform: "none", py: 1 }}
          >
            {isDeleting ? "Processing..." : "Remove entire sector assignment"}
          </Button>
          <Button
            variant="text"
            onClick={handleCloseDelete}
            disabled={isDeleting}
            sx={{ fontWeight: 600, color: "text.secondary" }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* REUSABLE SNACKBAR NOTIFICATION */}
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
}

export default ViewPayrollApprovers;
